import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, Zap, Play } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const ACTION_LABELS = { notify_admin: "Notify admin", flag_shipments: "Flag shipments", reduce_provider_priority: "Reduce provider priority", finance_alert: "Create finance alert" };
const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-shipany-blue focus:outline-none";

export default function AdminAutomation() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/automation-rules").then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/automation-runs").then(({ data }) => setRuns(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/admin/automation-rules", editing);
      toast.success("Automation saved — evaluated every 15 minutes");
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openNew = () => setEditing({ name: "", enabled: true, metric: "rto_high_shipments", op: ">=", threshold: 3, provider_code: "", actions: ["notify_admin"] });

  return (
    <div data-testid="admin-automation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Automation Engine</h1>
          <p className="text-sm text-slate-500">Visual IF → THEN ops automation. Evaluated by cron every 15 minutes.</p>
        </div>
        <button onClick={openNew} data-testid="automation-new-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue"><Plus className="h-4 w-4" /> New Automation</button>
      </div>

      <div className="mt-6 space-y-4" data-testid="automation-list">
        {(data?.rules || []).map((r) => (
          <div key={r.rule_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`automation-${r.rule_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Zap className={`h-5 w-5 ${r.enabled ? "text-amber-500" : "text-slate-300"}`} />
                <p className="font-display font-bold text-shipany-navy">{r.name}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={async () => { await api.post("/admin/automation-rules", { ...r, enabled: !r.enabled }); load(); }} data-testid={`automation-toggle-${r.rule_id}`}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold ${r.enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{r.enabled ? "ENABLED" : "DISABLED"}</button>
                <button onClick={() => setEditing(r)} data-testid={`automation-edit-${r.rule_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">Edit</button>
                <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/admin/automation-rules/${r.rule_id}`); load(); } }} data-testid={`automation-delete-${r.rule_id}`}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-lg bg-shipany-navy px-3 py-1.5 font-bold text-white">IF</span>
              <span className="rounded-lg bg-slate-50 px-3 py-1.5 font-medium text-slate-600">
                {data?.metrics.find((m) => m.key === r.metric)?.label || r.metric} <b className="mono text-shipany-blue">{r.op} {r.threshold}</b>{r.provider_code ? ` (${r.provider_code})` : ""}
              </span>
              <span className="rounded-lg bg-shipany-blue px-3 py-1.5 font-bold text-white">THEN</span>
              {r.actions.map((a) => <span key={a} className="rounded-lg bg-amber-50 px-3 py-1.5 font-semibold text-amber-700">{ACTION_LABELS[a]}</span>)}
            </div>
          </div>
        ))}
        {data && !data.rules.length && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No automations yet.</div>}
      </div>

      <h2 className="font-display mt-10 flex items-center gap-2 text-lg font-bold text-shipany-navy"><Play className="h-4 w-4 text-shipany-cyan" /> Recent Runs</h2>
      <div className="mt-3 space-y-2" data-testid="automation-runs">
        {runs.slice(0, 12).map((r) => (
          <div key={r.run_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs">
            <span className="font-semibold text-shipany-navy">{r.rule_name}</span>
            <span className="mono text-slate-500">value = {r.metric_value}</span>
            <span className="text-slate-400">{r.results.join(", ")}</span>
            <span className="ml-auto text-slate-400">{fmtDateTime(r.at)}</span>
          </div>
        ))}
        {!runs.length && <p className="text-sm text-slate-400">No automation runs yet.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="automation-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy">{editing.rule_id ? "Edit" : "New"} Automation</h2><button onClick={() => setEditing(null)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3.5">
              <input data-testid="automation-name" className={`${inputCls} w-full`} placeholder="Name *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">IF</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <select data-testid="automation-metric" className={`${inputCls} flex-1`} value={editing.metric} onChange={(e) => setEditing({ ...editing, metric: e.target.value })}>
                    {(data?.metrics || []).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                  <select data-testid="automation-op" className={`${inputCls} w-20`} value={editing.op} onChange={(e) => setEditing({ ...editing, op: e.target.value })}>
                    <option value=">=">≥</option><option value="<=">≤</option>
                  </select>
                  <input data-testid="automation-threshold" type="number" className={`${inputCls} mono w-24`} value={editing.threshold} onChange={(e) => setEditing({ ...editing, threshold: e.target.value })} />
                </div>
                {editing.metric === "provider_error_rate" && (
                  <select data-testid="automation-provider" className={`${inputCls} mt-2 w-full`} value={editing.provider_code} onChange={(e) => setEditing({ ...editing, provider_code: e.target.value })}>
                    <option value="">All providers</option>
                    {["shiprocket", "shipmozo", "bigship", "icarry"].map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
                  </select>
                )}
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">THEN</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(ACTION_LABELS).map(([k, l]) => (
                    <button key={k} type="button" data-testid={`automation-action-${k}`}
                      onClick={() => setEditing({ ...editing, actions: editing.actions.includes(k) ? editing.actions.filter((a) => a !== k) : [...editing.actions, k] })}
                      className={`rounded-lg px-3 py-1.5 text-xs font-bold ${editing.actions.includes(k) ? "bg-shipany-blue text-white" : "bg-white text-slate-500 border border-slate-200"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <input type="checkbox" data-testid="automation-enabled" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
                Enabled
              </label>
            </div>
            <button onClick={save} data-testid="automation-save-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Save Automation</button>
          </div>
        </div>
      )}
    </div>
  );
}
